/**
 * A one-shot success notice a screen leaves behind for whatever screen a `navigate.resetTo` is
 * about to land on — the Shabbat panel's save confirmation, so far.
 *
 * **Module state, not a route param.** The route's own shape is frozen to `flows.md`'s map
 * (`router/routes.ts`'s comment on `RouteId`) and a toast is not a location — `/gates/{id}` means
 * the same gate whether or not something was just saved on the way here, so it carries no query
 * string and never will.
 *
 * **Read-once and gate-scoped.** `take()` clears what it returns, so a resident routed to the
 * same gate later by an unrelated path — a tap from Home, a deep link — never inherits somebody
 * else's stale "Saved". A module-level variable rather than `sessionStorage`: this exists only to
 * bridge one navigation already in flight and must not survive a reload — nothing here is worth
 * restoring a resident to.
 */
import type { GateId } from '@selogate/core'

let pending: { readonly gateId: GateId; readonly message: string } | undefined

export function setPendingGateToast(gateId: GateId, message: string): void {
  pending = { gateId, message }
}

/** Consumes the pending toast for this gate, if any. Returns `undefined` on every later call. */
export function takePendingGateToast(gateId: GateId): string | undefined {
  if (pending === undefined || pending.gateId !== gateId) return undefined
  const message = pending.message
  pending = undefined
  return message
}
